import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Phone, ArrowRight, TrendingUp, Clock, Zap, DollarSign, BarChart3, Users, Shield } from "lucide-react";
import { industries, getIconComponent, industryImages } from "@/data/industries";
import { Helmet } from "react-helmet";
import BlogArticleLayout from "@/components/BlogArticleLayout";

// Top industries to keep indexed (matching sitemap)
const TOP_INDUSTRIES = new Set([
  "medical-clinics", "dental-practices", "real-estate-agencies", "hotels-resorts",
  "restaurants-dining", "law-firms", "hvac-services", "auto-dealerships",
  "insurance-agencies", "salons-barbershops", "plumbing-services", "fitness-studios",
  "ecommerce-stores", "saas-companies", "veterinary-clinics",
]);

const IndustryDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const industry = industries.find(i => i.id === id);

  if (!industry) {
    return <Navigate to="/industries" replace />;
  }

  const IconComponent = getIconComponent(industry.icon);
  const relatedIndustries = industries
    .filter(i => i.category === industry.category && i.id !== industry.id)
    .slice(0, 4);

  const shareUrl = `https://call24x7.ai/industries/${industry.id}`;
  const pageTitle = t('seo.industry.titleTemplate', { industry: industry.name });
  const metaDescription = t('seo.industry.descriptionTemplate', { industry: industry.name.toLowerCase() });
  const keywords = `AI phone agent ${industry.name}, 24/7 AI answering service ${industry.name}, ${industry.name} call automation, AI receptionist ${industry.name}, ${industry.category} AI customer service`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `AI Phone Agents for ${industry.name}`,
    description: industry.description,
    provider: { "@type": "Organization", name: "Call24x7.AI", url: "https://call24x7.ai" },
    serviceType: "AI Phone Automation",
    areaServed: "Worldwide",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `What can AI phone agents do for ${industry.name}?`,
        acceptedAnswer: { "@type": "Answer", text: industry.useCases.join(". ") + "." },
      },
      {
        "@type": "Question",
        name: `How much does an AI phone agent cost for ${industry.name}?`,
        acceptedAnswer: { "@type": "Answer", text: `AI phone agents for ${industry.name} typically cost $49–199/month — a fraction of hiring dedicated phone staff at $35,000+/year. Call24x7.AI offers a free trial so you can test before committing.` },
      },
      {
        "@type": "Question",
        name: `What are the benefits of AI phone agents for ${industry.name}?`,
        acceptedAnswer: { "@type": "Answer", text: industry.benefits.join(". ") + "." },
      },
      {
        "@type": "Question",
        name: `What tools do AI phone agents integrate with for ${industry.name}?`,
        acceptedAnswer: { "@type": "Answer", text: `Our AI phone agents integrate with popular ${industry.category} tools including ${industry.integrations.join(", ")}.` },
      },
      {
        "@type": "Question",
        name: `How long does it take to set up an AI phone agent for ${industry.name}?`,
        acceptedAnswer: { "@type": "Answer", text: `With Call24x7.AI, you can set up an AI phone agent for your ${industry.name.toLowerCase()} business in under 60 seconds. Simply enter your website URL and the AI automatically creates a custom phone agent tailored to your industry.` },
      },
    ],
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://call24x7.ai" },
      { "@type": "ListItem", position: 2, name: "Industries", item: "https://call24x7.ai/industries" },
      { "@type": "ListItem", position: 3, name: industry.name, item: shareUrl },
    ],
  };

  const heroImg = industryImages[industry.id];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content={TOP_INDUSTRIES.has(industry.id) ? "index, follow" : "noindex, follow"} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:site_name" content="Call24x7.AI" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>
      </Helmet>

      <BlogArticleLayout
        title={`24/7 AI Phone Agent for ${industry.name}`}
        category={industry.category}
        date="2026"
        readTime="5 min read"
        author="Call24x7 Team"
        heroImage={heroImg}
        heroAlt={`AI Phone Agents for ${industry.name} — 24/7 automated customer communication`}
      >
        {/* Intro */}
        <p className="text-xl text-muted-foreground mb-6">
          {industry.description}
        </p>

        {/* Quick Stats Banner */}
        <Card className="bg-muted/50 border-primary/20 my-8">
          <CardContent className="pt-6">
            <p className="font-semibold mb-3">📊 At a Glance — AI for {industry.name}:</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {industry.stats.map((stat, i) => (
                <div key={i} className="text-center p-4 rounded-xl bg-background border border-border">
                  <div className="text-3xl font-bold text-primary">{stat.value}</div>
                  <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
              <div className="text-center p-4 rounded-xl bg-background border border-border">
                <div className="text-3xl font-bold text-primary">60s</div>
                <div className="text-sm text-muted-foreground mt-1">Setup Time</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* The Problem Section */}
        <h2 className="text-2xl font-bold mt-10 mb-4">
          The Problem: Why {industry.name} Are Losing Customers
        </h2>
        <p className="mb-4">
          In the {industry.category.toLowerCase()} industry, every missed call is a missed opportunity. Studies show that <strong>85% of callers who can't reach a business won't call back</strong> — they'll call your competitor instead. For {industry.name.toLowerCase()}, this is especially costly because:
        </p>
        <ul className="space-y-2 my-4">
          <li className="flex items-start gap-3">
            <span className="text-destructive font-bold mt-0.5">✗</span>
            <span>Peak hours mean phone lines are constantly busy</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-destructive font-bold mt-0.5">✗</span>
            <span>After-hours calls go straight to voicemail (and are often never returned)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-destructive font-bold mt-0.5">✗</span>
            <span>Staff spend hours on repetitive calls instead of high-value work</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-destructive font-bold mt-0.5">✗</span>
            <span>Hiring dedicated phone staff costs $35,000+/year per person</span>
          </li>
        </ul>

        {/* Solution Section */}
        <h2 className="text-2xl font-bold mt-10 mb-4">
          The Solution: AI Phone Agents Built for {industry.name}
        </h2>
        <p className="mb-4">
          Call24x7.AI provides intelligent, voice-based AI agents specifically designed for the {industry.category.toLowerCase()} industry. Our agents don't just answer calls — they handle complete workflows:
        </p>

        {/* Use Cases as rich cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          {industry.useCases.map((useCase, index) => (
            <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <div>
                <span className="font-medium">{useCase}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Cost Comparison Table */}
        <h2 className="text-2xl font-bold mt-10 mb-4">
          Cost Comparison: AI vs Human Staff for {industry.name}
        </h2>
        <p className="mb-4">
          See how an AI phone agent stacks up against traditional alternatives:
        </p>
        <div className="overflow-x-auto my-6">
          <table className="w-full border-collapse border border-border">
            <thead className="bg-muted">
              <tr>
                <th className="text-left py-3 px-4 border border-border">Feature</th>
                <th className="text-left py-3 px-4 border border-border">Call24x7 AI Agent</th>
                <th className="text-left py-3 px-4 border border-border">Human Staff</th>
                <th className="text-left py-3 px-4 border border-border">Voicemail</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4 border border-border font-medium">Monthly Cost</td>
                <td className="py-2 px-4 border border-border text-primary font-semibold">$49–199</td>
                <td className="py-2 px-4 border border-border">$2,900+</td>
                <td className="py-2 px-4 border border-border">Free</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-border font-medium">Availability</td>
                <td className="py-2 px-4 border border-border text-primary font-semibold">24/7/365</td>
                <td className="py-2 px-4 border border-border">Business hours</td>
                <td className="py-2 px-4 border border-border">24/7 (no interaction)</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-border font-medium">Simultaneous Calls</td>
                <td className="py-2 px-4 border border-border text-primary font-semibold">Unlimited</td>
                <td className="py-2 px-4 border border-border">1 per person</td>
                <td className="py-2 px-4 border border-border">1</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-border font-medium">Handles {industry.useCases[0]?.split(' ').slice(0, 2).join(' ')}</td>
                <td className="py-2 px-4 border border-border text-primary font-semibold">✓ Automatically</td>
                <td className="py-2 px-4 border border-border">✓ Manually</td>
                <td className="py-2 px-4 border border-border">✗</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-border font-medium">Setup Time</td>
                <td className="py-2 px-4 border border-border text-primary font-semibold">60 seconds</td>
                <td className="py-2 px-4 border border-border">2–6 weeks</td>
                <td className="py-2 px-4 border border-border">Minutes</td>
              </tr>
              <tr>
                <td className="py-2 px-4 border border-border font-medium">Multilingual</td>
                <td className="py-2 px-4 border border-border text-primary font-semibold">✓ 30+ languages</td>
                <td className="py-2 px-4 border border-border">Limited</td>
                <td className="py-2 px-4 border border-border">✗</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-muted-foreground">
          For a detailed cost analysis, see our{" "}
          <Link to="/blog/ai-vs-human-agents-cost-comparison" className="text-primary underline hover:no-underline">
            AI vs Human Agents cost comparison
          </Link>.
        </p>

        {/* Benefits Section */}
        <h2 className="text-2xl font-bold mt-10 mb-4">
          Key Benefits for {industry.name}
        </h2>
        <div className="space-y-4 my-6">
          {industry.benefits.map((benefit, index) => {
            const icons = [TrendingUp, DollarSign, Clock, Shield, Users, Zap, BarChart3];
            const BenefitIcon = icons[index % icons.length];
            return (
              <div key={index} className="flex items-start gap-4 p-5 rounded-xl bg-muted/50 border border-border">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <BenefitIcon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">{benefit}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* How It Works */}
        <h2 className="text-2xl font-bold mt-10 mb-4">
          How It Works — 3 Simple Steps
        </h2>
        <div className="space-y-4 my-6">
          {[
            { step: "1", title: "Enter your website URL", desc: `Our AI analyzes your ${industry.name.toLowerCase()} website and automatically understands your services, hours, and FAQs.` },
            { step: "2", title: "Customize your agent", desc: `Fine-tune responses for common ${industry.category.toLowerCase()} scenarios — ${industry.useCases.slice(0, 2).join(", ").toLowerCase()}, and more.` },
            { step: "3", title: "Go live in 60 seconds", desc: "Forward your business phone number to your new AI agent and start handling calls 24/7 immediately." },
          ].map((item) => (
            <div key={item.step} className="flex items-start gap-4 p-5 rounded-xl border border-border">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 font-bold text-lg">
                {item.step}
              </div>
              <div>
                <p className="font-semibold text-foreground">{item.title}</p>
                <p className="text-muted-foreground text-sm mt-1">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Integrations */}
        <h2 className="text-2xl font-bold mt-10 mb-4">
          Integrations for {industry.name}
        </h2>
        <p className="mb-4">
          Our AI phone agents connect seamlessly with the tools your {industry.name.toLowerCase()} business already uses:
        </p>
        <div className="flex flex-wrap gap-2 my-6">
          {industry.integrations.map((integration, index) => (
            <Badge key={index} variant="outline" className="px-4 py-2 text-sm">
              {integration}
            </Badge>
          ))}
        </div>

        {/* Mid-article CTA */}
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20 my-10">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">Ready to Automate Your {industry.name} Calls?</h3>
            <p className="text-muted-foreground mb-4">
              Set up your AI phone agent in 60 seconds — no coding, no contracts.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild>
                <Link to="/">
                  <Phone className="mr-2 h-4 w-4" /> Try Free Demo
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <a href="https://manage.call24x7.ai/signup">
                  Get Started Free
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <h2 className="text-2xl font-bold mt-10 mb-4">
          Frequently Asked Questions
        </h2>
        <div className="space-y-6 my-6">
          {faqSchema.mainEntity.map((faq, i) => (
            <div key={i}>
              <h3 className="font-semibold mb-2">{faq.name}</h3>
              <p className="text-muted-foreground">{faq.acceptedAnswer.text}</p>
            </div>
          ))}
        </div>

        {/* Related Industries */}
        {relatedIndustries.length > 0 && (
          <>
            <h2 className="text-2xl font-bold mt-10 mb-4">
              Related {industry.category} Industries
            </h2>
            <Card className="bg-muted/50 border-border my-6">
              <CardContent className="pt-6">
                <p className="font-semibold mb-3">🏢 Explore more {industry.category.toLowerCase()} solutions:</p>
                <ul className="space-y-2">
                  {relatedIndustries.map((related) => {
                    const RelatedIcon = getIconComponent(related.icon);
                    return (
                      <li key={related.id}>
                        <Link
                          to={`/industries/${related.id}`}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-background transition-colors text-primary underline-offset-2 hover:underline"
                        >
                          <RelatedIcon className="h-5 w-5 text-primary flex-shrink-0" />
                          <span className="flex-1">AI Phone Agents for {related.name}</span>
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          </>
        )}

        {/* Internal Links */}
        <Card className="bg-muted/50 border-border my-6">
          <CardContent className="pt-6">
            <p className="font-semibold mb-3">📚 Learn More:</p>
            <ul className="space-y-1 text-sm">
              <li>• <Link to="/blog/what-is-ai-answering-service" className="text-primary underline hover:no-underline">What is an AI Answering Service?</Link></li>
              <li>• <Link to="/blog/ai-vs-human-agents-cost-comparison" className="text-primary underline hover:no-underline">AI vs Human Agents: Full Cost Breakdown</Link></li>
              <li>• <Link to="/blog/best-ai-phone-agent-software" className="text-primary underline hover:no-underline">7 Best AI Phone Agent Platforms (2026)</Link></li>
              <li>• <Link to="/blog/how-to-set-up-ai-call-center" className="text-primary underline hover:no-underline">How to Set Up an AI Call Center</Link></li>
              <li>• <Link to="/industries" className="text-primary underline hover:no-underline">View all 60+ industries →</Link></li>
            </ul>
          </CardContent>
        </Card>

        {/* Final CTA */}
        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-10">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">Transform Your {industry.name} Today</h3>
            <p className="text-muted-foreground mb-4">
              Join thousands of {industry.category.toLowerCase()} businesses using AI phone agents to capture more leads, reduce costs, and never miss a call again.
            </p>
            <Button size="lg" onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              Start Free Trial — No Credit Card Required
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default IndustryDetail;
